import { ref } from 'vue'
import { httpService } from '../services/httpService'
import { config } from '../services/configService'
import { log } from '../utils/DebugLogger'
import type { AiCredits_KpisResponseDados } from '../contracts/AiCredits_KpisResponse'

// Import side-effect: registers the contract with the responseContract registry.
import '../contracts/AiCredits_KpisResponse'

/**
 * useCreditKpis — SOLE network owner for ai/credits/kpis/* routes.
 *
 * Thin composable that mirrors useCreditWallet's structure (sistemaUrl via
 * config.api.sistemaHost, responseContract 'AiCredits_KpisResponse'). Does NOT
 * modify useCreditWallet — the two composables are complementary, not overlapping.
 *
 * Franchise-family only: the KpisResponse is ISP-disjoint from the Admin family
 * (no cost/markup/margin fields). The caller is responsible for gating to Full.
 */

function sistemaUrl(path: string): string {
  return `${config.api.sistemaHost}/${path}`
}

// ── Module-scope singleton state ─────────────────────────────────────────────

const kpis = ref<AiCredits_KpisResponseDados | null>(null)
const loading = ref<boolean>(false)

// ── Public composable ─────────────────────────────────────────────────────────

export function useCreditKpis() {
  /**
   * fetchKpis — GET ai/credits/kpis/{franquiaId}?period=.
   *
   * Populates the module-scope `kpis` ref. On error, logs and rethrows.
   */
  async function fetchKpis(franquiaId: number, period?: string): Promise<void> {
    loading.value = true
    try {
      const params = period ? `?period=${encodeURIComponent(period)}` : ''
      const raw = await httpService.get(
        sistemaUrl(`ai/credits/kpis/${franquiaId}${params}`),
        { responseContract: 'AiCredits_KpisResponse', skipErrorDisplay: false },
      )
      const resp = raw as { dados: AiCredits_KpisResponseDados }
      kpis.value = resp.dados
      log('useCreditKpis', 'fetchKpis', 'KPIs carregados', { franquiaId, period })
    } catch (err) {
      log('useCreditKpis', 'fetchKpis', 'Erro ao carregar KPIs', {
        franquiaId,
        message: (err as Error)?.message,
      })
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * _reset — wipes module-scope state back to initial values.
   * Call in afterEach to prevent cross-test leakage.
   */
  function _reset(): void {
    kpis.value = null
    loading.value = false
  }

  return {
    kpis,
    loading,
    fetchKpis,
    _reset,
  }
}
